import React from "react";
import { Box, Grid, Paper, Skeleton, Stack } from "@mui/material";

/**
 * Skeleton loader for the Admin Staking Pools page. Mirrors the layout
 * (back link → header → KPI cards → filters card → pool cards) so the
 * loading state has the same shape as the loaded page.
 */
export default function AdminStakingPoolsSkeleton() {
  return (
    <Box>
      {/* Back link */}
      <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />

      {/* Header row */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 1.5 }} />
        <Box>
          <Skeleton variant="text" width={200} height={36} />
          <Skeleton variant="text" width={340} height={20} />
        </Box>
      </Stack>

      {/* KPI cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.statCard,
              }}
            >
              <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1, mb: 2 }} />
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton variant="text" width="45%" height={28} />
              <Skeleton variant="text" width="55%" height={14} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Skeleton variant="rounded" height={40} sx={{ flex: 1, borderRadius: 1.25 }} />
          <Skeleton variant="rounded" width={180} height={40} sx={{ borderRadius: 1.25 }} />
        </Stack>
      </Paper>

      {/* Pool card rows */}
      <Stack spacing={2.5}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              p: 3,
              borderRadius: 1.5,
              bgcolor: "background.subtle",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
              mb={3}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 1.25 }} />
                <Box>
                  <Skeleton variant="text" width={200} height={26} />
                  <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                    <Skeleton variant="rounded" width={110} height={22} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 999 }} />
                  </Stack>
                  <Skeleton variant="text" width={160} height={18} sx={{ mt: 0.75 }} />
                </Box>
              </Stack>
              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Skeleton variant="text" width={80} height={16} sx={{ ml: { sm: "auto" } }} />
                <Skeleton variant="text" width={70} height={36} sx={{ ml: { sm: "auto" } }} />
              </Box>
            </Stack>

            <Grid container spacing={3}>
              {[0, 1, 2, 3].map((j) => (
                <Grid size={{ xs: 6, md: 3 }} key={j}>
                  <Skeleton variant="text" width={70} height={16} />
                  <Skeleton variant="text" width={90} height={22} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
